import React, { useState, useEffect, useMemo } from "react";
import { useCreateArticleGenerator } from "@packages/api/madlenAPI";
import ModeToggle from "@features/common/components/mode-toggle";
import { FileUploader } from "@core/file-uploader";
import { Button, TextLinear } from "@core/index";
import {
  ArticleGeneratorCreateRequestDtoGenerationMode,
  ArticleGeneratorCreateRequestDtoGenerationType,
  GetTopicsCourseType,
} from "@packages/api/madlenAPI.schemas";
import { getEnvironment, firstTopicOfCourse } from "@packages/utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UnitTopicSelector } from "@packages/components/unit-topic-selector";
import { UnitOutcomeSelector } from "@features/assignments/AssignmentGenerator/components/unit-outcome-selector";
import GradeSelector from "@features/tools/components/grade-selector";
import CourseSelector from "@features/assignments/AssignmentGenerator/components/course-selector";
import { CourseData } from "@packages/constant/CourseData";
import { useToast } from "@packages/contexts/ToastContext";
import CourseAndAudianceInput, {
  processPrompt,
} from "@features/tools/components/create-modal/CourseAndAudianceInput";

const CHARACTER_LIMIT = 200000;

type MainMode = "text" | "document" | "topic";

function CreateArticleView() {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const [mainMode, setMainMode] = useState<MainMode>("topic");
  const [input, setInput] = useState("");
  const [gradeLevel, setGradeLevel] = useState<number>(9); // Default to grade 9
  const [courseName, setCourseName] = useState<GetTopicsCourseType | "UNKNOWN">(
    "UNKNOWN",
  );
  const [uploadedFiles, setUploadedFiles] = useState<TeacherDocument[]>([]);
  const [isTopicSelection, setIsTopicSelection] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState<number[]>([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState<number[]>([]);
  const [isOutcomeSelection, setIsOutcomeSelection] = useState(
    i18n.language === "tr",
  );
  // Ensure grade level is treated as a number for calculations
  const gradeAsNumber = Number(gradeLevel);

  // Check if selected grade is in 1-4 range
  const isJuniorGrade = gradeAsNumber >= 1 && gradeAsNumber <= 4;

  const { mutateAsync: createArticle, isPending: isGenerating } =
    useCreateArticleGenerator();

  useEffect(() => {
    setCourseName(
      (i18n.language === "tr"
        ? "HISTORY"
        : "AP_2025_WORLD_HISTORY_MODERN") as GetTopicsCourseType,
    );
    setIsOutcomeSelection(i18n.language === "tr");
    setSelectedTopics([]);
    setSelectedOutcomes([]);
  }, [i18n.language]);

  const acceptedFileTypes = {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "text/plain": [".txt"],
    "image/png": [".png"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/jpg": [".jpg", ".jpeg"],
  };

  const navigate = useNavigate();

  const handleGradeChange = (grade: number) => {
    const availableCourses = Object.entries(CourseData).filter(([, value]) =>
      value.visible(grade),
    );
    const firstAvailableCourse =
      availableCourses[0]?.[0] as GetTopicsCourseType;
    const doesPreviousCourseExist = availableCourses.some(
      ([key]) => key === courseName,
    );
    setSelectedTopics([]);
    setSelectedOutcomes([]);

    // If switching to a junior grade (1-4), force PROMPT mode
    const isNewGradeJunior = grade >= 1 && grade <= 4;

    if (isNewGradeJunior || !firstAvailableCourse) {
      setIsTopicSelection(false);
      setIsOutcomeSelection((prev) => {
        return prev && firstAvailableCourse ? i18n.language === "tr" : false;
      });
    }

    setGradeLevel(grade);
    setCourseName(
      doesPreviousCourseExist
        ? courseName
        : (firstAvailableCourse ?? "UNKNOWN"),
    );
  };

  async function handleGenerate(e: React.FormEvent) {
    e.preventDefault();

    // Validate topic selection if in topic mode
    if (
      mainMode === "topic" &&
      isTopicSelection &&
      selectedTopics.length === 0
    ) {
      addToast({
        title: t("notifications.assignment.emptyTopics"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    // Validate outcome selection if in topic mode
    if (
      mainMode === "topic" &&
      isOutcomeSelection &&
      selectedOutcomes.length === 0
    ) {
      addToast({
        title: t("notifications.assignment.emptyOutcomes"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    // Validate for different modes
    if (
      (mainMode === "topic" &&
        !isTopicSelection &&
        !isOutcomeSelection &&
        !input.trim()) ||
      (mainMode === "text" && !input.trim()) ||
      (mainMode === "document" && uploadedFiles.length === 0)
    ) {
      addToast({
        title: t("articleGenerator.error.title"),
        description: t("articleGenerator.error.noInput"),
        variant: "error",
        duration: 3000,
      });
      return;
    }
    if (
      (mainMode === "text" || mainMode === "topic") &&
      input.length > CHARACTER_LIMIT
    ) {
      addToast({
        title: t("articleGenerator.error.tooLong"),
        description: t("articleGenerator.error.characterLimit", {
          limit: CHARACTER_LIMIT.toLocaleString(),
        }),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    try {
      let generationType;

      if (mainMode === "document") {
        generationType =
          ArticleGeneratorCreateRequestDtoGenerationType.TEACHER_DOCUMENT;
      } else if (mainMode === "topic") {
        if (isTopicSelection) {
          generationType =
            ArticleGeneratorCreateRequestDtoGenerationType.TOPIC_SELECTION;
        } else if (isOutcomeSelection) {
          generationType =
            ArticleGeneratorCreateRequestDtoGenerationType.MAARIF_LEARNING_OUTCOME;
        } else {
          generationType =
            ArticleGeneratorCreateRequestDtoGenerationType.PROMPT;
        }
      } else {
        generationType = ArticleGeneratorCreateRequestDtoGenerationType.PROMPT;
      }

      const result = await createArticle({
        data: {
          generationType,
          prompt: mainMode === "topic" ? input : undefined,
          rawArticle: mainMode === "text" ? input : undefined,
          generationMode:
            mainMode === "text"
              ? ArticleGeneratorCreateRequestDtoGenerationMode.REFORMAT
              : ArticleGeneratorCreateRequestDtoGenerationMode.PROMPT,
          teacherDocumentIds:
            mainMode === "document"
              ? uploadedFiles.map((f) => f.id).filter(Boolean)
              : undefined,
          environment: getEnvironment(),

          courseName: mainMode === "topic" ? courseName : "UNKNOWN",
          gradeLevel: mainMode === "topic" ? gradeAsNumber : -1,
        },
      });
      if (result?.id) {
        navigate(`/content/article-generator/article/${result.id}`);
      }
    } catch (e) {
      addToast({
        title: t("articleGenerator.error.title"),
        description: t("articleGenerator.error.description"),
        variant: "error",
        duration: 3000,
      });
    }
  }

  const handleFileUploadError = (error: string) => {
    addToast({
      title: t("articleGenerator.error.title"),
      description: error,
      variant: "error",
      duration: 3000,
    });
  };

  const handleFileUploadSuccess = async (url: string, response?: any) => {
    if (!response) {
      addToast({
        title: t("articleGenerator.error.title"),
        variant: "error",
        duration: 3000,
      });
      return;
    }
    setUploadedFiles((prevFiles) => {
      const newFiles = [...prevFiles, response];
      return newFiles;
    });
  };

  // Show grade and course selection only for prompt mode, not for article (reformat) mode
  const shouldShowGradeAndCourse = mainMode === "topic";
  const placeholderTopic = useMemo(() => {
    return firstTopicOfCourse(Number(gradeLevel), courseName);
  }, [gradeLevel, courseName]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50 dark:from-gray-900 dark:to-teal-900">
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-teal-200/50 dark:border-teal-700/50 mb-16">
          <div className="space-y-8">
            <ModeToggle
              value={mainMode}
              hidePDFInput={true}
              onChange={(value) => {
                setMainMode(value as MainMode);
                setIsOutcomeSelection(
                  value === "topic" && i18n.language === "tr",
                );
                setIsTopicSelection(false);
                setInput("");
                setUploadedFiles([]);
              }}
              title={t("articleGenerator.selectInputType")}
              theme="green"
              newButtonKey="topic"
              newButtonTitle="articleGenerator.topicMode"
            />

            {/* Grade Level Selector - only for prompt mode */}
            {shouldShowGradeAndCourse && (
              <div className="space-y-2 flex flex-col items-start">
                <label className="inline-block text-sm font-medium text-teal-700 dark:text-teal-300">
                  {t("hooks.form.gradeLevel")}
                </label>
                <div className="w-full">
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                    <GradeSelector
                      selectedGrade={gradeLevel}
                      setSelectedGrade={handleGradeChange}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Course Selector - only shown for non-junior grades in prompt mode */}
            {shouldShowGradeAndCourse && gradeAsNumber !== -1 && (
              <div className="space-y-2 flex flex-col items-start">
                <label className="inline-block text-sm font-medium text-teal-700 dark:text-teal-300">
                  {t("hooks.form.courseName")}
                </label>
                <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-3">
                  <CourseSelector
                    selectedGrade={gradeLevel}
                    filteredCourses={
                      isOutcomeSelection
                        ? [GetTopicsCourseType.DIGITAL_CITIZENSHIP]
                        : []
                    }
                    selectedCourse={courseName as GetTopicsCourseType}
                    setSelectedCourse={(course) => {
                      setSelectedTopics([]);
                      setCourseName(course);
                    }}
                  />
                </div>
              </div>
            )}
            {gradeAsNumber === -1 && mainMode === "topic" && (
              <CourseAndAudianceInput
                input={input}
                setInput={setInput}
                labelClass={"text-teal-700 dark:text-teal-300"}
              />
            )}
            {/* Selection Mode Buttons - only shown for non-junior grades in prompt mode */}
            {shouldShowGradeAndCourse && gradeAsNumber !== -1 && (
              <div className="flex flex-col md:flex-row flex-wrap justify-center gap-4">
                <Button
                  type="button"
                  className="flex-1"
                  onClick={() => {
                    setIsTopicSelection(false);
                    setIsOutcomeSelection(false);
                    setInput("");
                    setSelectedTopics([]);
                    setSelectedOutcomes([]);
                  }}
                  variant="solid"
                  size="md"
                  rounded="lg"
                  textColor={
                    !isTopicSelection && !isOutcomeSelection
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-400"
                  }
                  bgColor={
                    !isTopicSelection && !isOutcomeSelection
                      ? "bg-gradient-to-r from-teal-500 to-emerald-600 dark:from-teal-400 dark:to-emerald-500 shadow-lg"
                      : "bg-gray-100 dark:bg-gray-700"
                  }
                >
                  {t("common.freeInput")}
                </Button>
                {!isJuniorGrade && (
                  <>
                    <div className="flex items-center justify-center">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t("common.or")}
                      </div>
                    </div>

                    <Button
                      type="button"
                      className="flex-1"
                      onClick={() => {
                        setIsTopicSelection(true);
                        setIsOutcomeSelection(false);
                        setInput("");
                        setSelectedOutcomes([]);
                      }}
                      variant="solid"
                      size="md"
                      rounded="lg"
                      textColor={
                        isTopicSelection
                          ? "text-white"
                          : "text-gray-600 dark:text-gray-400"
                      }
                      bgColor={
                        isTopicSelection
                          ? "bg-gradient-to-r from-teal-500 to-emerald-600 dark:from-teal-400 dark:to-emerald-500 shadow-lg"
                          : "bg-gray-100 dark:bg-gray-700"
                      }
                    >
                      {t("common.topicSelection")}
                    </Button>
                  </>
                )}
                {i18n.language !== "en" && (
                  <>
                    <div className="flex items-center justify-center">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t("common.or")}
                      </div>
                    </div>
                    <Button
                      type="button"
                      className="flex-1"
                      onClick={() => {
                        setIsTopicSelection(false);
                        setIsOutcomeSelection(true);
                        const availableCourses = Object.entries(
                          CourseData,
                        ).filter(
                          ([, value]) =>
                            value.visible(gradeAsNumber) &&
                            value.hasMaarifOutcomes,
                        );
                        const firstAvailableCourse =
                          availableCourses[0]?.[0] as GetTopicsCourseType;
                        const doesPreviousCourseExist = availableCourses.some(
                          ([key]) => key === courseName,
                        );
                        setCourseName(
                          doesPreviousCourseExist
                            ? courseName
                            : (firstAvailableCourse ?? "UNKNOWN"),
                        );
                        setInput("");
                        setSelectedTopics([]);
                      }}
                      variant="solid"
                      size="md"
                      rounded="lg"
                      textColor={
                        isOutcomeSelection
                          ? "text-white"
                          : "text-gray-600 dark:text-gray-400"
                      }
                      bgColor={
                        isOutcomeSelection
                          ? "bg-gradient-to-r from-teal-500 to-emerald-600 dark:from-teal-400 dark:to-emerald-500 shadow-lg"
                          : "bg-gray-100 dark:bg-gray-700"
                      }
                    >
                      {t("common.maarifOutcomeSelection")}
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Topic and Outcome Selectors */}
            {shouldShowGradeAndCourse && (
              <div className="space-y-4">
                {isTopicSelection && gradeAsNumber !== -1 ? (
                  <UnitTopicSelector
                    grade={gradeLevel}
                    courseType={courseName as GetTopicsCourseType}
                    selectedTopics={selectedTopics}
                    setSelectedTopics={(topicIds, topicNames) => {
                      setSelectedTopics(topicIds);
                      setInput(topicNames ? topicNames.join(", ") : "");
                    }}
                  />
                ) : isOutcomeSelection && gradeAsNumber !== -1 ? (
                  <UnitOutcomeSelector
                    grade={gradeLevel}
                    courseType={courseName as GetTopicsCourseType}
                    selectedOutcomes={selectedOutcomes}
                    isMaarif={true}
                    setSelectedOutcomes={(outcomeIds, outcomeTexts) => {
                      setSelectedOutcomes(outcomeIds);
                      setInput(outcomeTexts ? outcomeTexts.join(", ") : "");
                    }}
                  />
                ) : (
                  <div className="space-y-2">
                    <label
                      className={`inline-block text-sm font-medium text-teal-700 dark:text-teal-300`}
                    >
                      {isJuniorGrade
                        ? t("hooks.form.promptJunior")
                        : t("hooks.form.lessonTopic")}
                    </label>
                    <input
                      type="text"
                      data-testid="lesson-topic-input"
                      value={gradeAsNumber === -1 ? undefined : input}
                      onChange={(e) => {
                        if (gradeAsNumber === -1) {
                          const newPrompt = processPrompt(
                            e.target.value,
                            input,
                            i18n.language,
                          );
                          setInput(newPrompt);
                        } else {
                          setInput(e.target.value);
                        }
                      }}
                      className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
                      placeholder={
                        isJuniorGrade || gradeAsNumber === -1
                          ? t("hooks.form.promptJuniorPlaceholder")
                          : (i18n.language === "tr" ? "Örnek: " : "Example: ") +
                            placeholderTopic?.topic
                      }
                    />
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              {(mainMode === "text" || mainMode === "document") && (
                <TextLinear
                  variant={"label"}
                  from="from-teal-600 dark:from-teal-400"
                  to="to-teal-500 dark:to-teal-300"
                  className="inline-block"
                >
                  {mainMode === "text"
                    ? t("articleGenerator.textMode.title")
                    : null}
                  {mainMode === "document"
                    ? t("articleGenerator.documentMode.title")
                    : null}
                </TextLinear>
              )}
              {mainMode === "document" && (
                <FileUploader
                  accept={acceptedFileTypes}
                  onUploadSuccess={handleFileUploadSuccess}
                  onFileDelete={() => setUploadedFiles([])}
                  onUploadError={handleFileUploadError}
                  dragActiveText="Drop PDF here"
                  dragInactiveText="Upload PDF"
                  uploadColor="green"
                  extractColor="green"
                  successColor="green"
                  shouldExtractContent
                />
              )}

              {/* Input field for article mode or prompt mode when not using selection */}
              {mainMode === "text" && (
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  rows={mainMode === "text" ? 12 : 6}
                  className="w-full rounded-xl border-2 border-teal-200 dark:border-teal-700 p-4 text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200"
                  placeholder={t("articleGenerator.textMode.placeholder")}
                />
              )}
            </div>

            <Button
              variant="solid"
              size="lg"
              rounded="xl"
              fullWidth
              bgColor="bg-teal-500 hover:bg-teal-600"
              textColor="text-white"
              className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-teal-500/50"
              type="button"
              onClick={handleGenerate}
              disabled={
                isGenerating ||
                (mainMode === "text" && !input) ||
                (mainMode === "topic" &&
                  !isTopicSelection &&
                  !isOutcomeSelection &&
                  !input) ||
                (mainMode === "document" && uploadedFiles.length === 0) ||
                (isTopicSelection && selectedTopics.length === 0) ||
                (isOutcomeSelection && selectedOutcomes.length === 0)
              }
              data-testid="toolbox-create-button"
            >
              {t("articleGenerator.createButton")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateArticleView;
